import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

function routes($stateProvider, $urlRouterProvider) {
  const noRoute = {
    name: 'noRoute',
    url: '/404',
    component: 'noRoute'
  };

  const login = {
    name: 'login',
    url: '/login',
    component: 'login',
    resolve: {
      $title: () => 'Login'
    }
  };

  const logout = {
    name: 'logout',
    url: '/logout',
    component: 'logout',
    onEnter: ['SpinnerService', SpinnerService => SpinnerService.start()]
  };

  const denied = {
    name: 'accessdenied',
    url: '/denied',
    component: 'denied'
  };

  //ADMIN ROUTES
  const admin = {
    name: 'admin',
    url: '/admin',
    abstract: true,
    component: 'admin'
  };

  const admin_users = {
    name: 'admin.users',
    url: '/users',
    component: 'users',
    data: { roles: ['Admin'] },
    resolve: {
      $title: () => 'Users',
      users: [
        'UserService',
        UserService => UserService.getAll({ include: 'user-roles,user-roles.group' })
      ]
    }
  };

  const admin_user_detail = {
    name: 'admin.user-detail',
    url: '/users/:userId',
    component: 'userDetail',
    data: { roles: ['Admin'] },
    resolve: {
      user: [
        'UserService',
        '$stateParams',
        (UserService, $stateParams) =>
          UserService.get($stateParams.userId, {
            include: 'user-roles,user-roles.group,user-roles.role'
          })
      ],
      $title: ['user', user => `Users - ${user.name || 'New'}`]
    }
  };

  const admin_user_role = {
    name: 'admin.user-role-detail',
    url: '/user-roles/:userRoleId?userId&siteId',
    component: 'userRoleDetail',
    resolve: {
      $title: () => 'User Role',
      manufacturers: ['ManufacturerService', ManufacturerService => ManufacturerService.getAll()],
      sites: ['SiteService', SiteService => SiteService.getAll()],
      users: ['UserService', UserService => UserService.getAll()],
      userRole: [
        'UserRoleService',
        '$stateParams',
        (UserRoleService, $stateParams) =>
          UserRoleService.get($stateParams.userRoleId, { include: 'role' })
      ]
    }
  };

  const admin_sites = {
    name: 'admin.sites',
    url: '/sites',
    component: 'sites',
    data: { roles: ['Admin'] },
    resolve: {
      $title: () => 'Sites',
      sites: ['SiteService', SiteService => SiteService.getAll()]
    }
  };

  const admin_site_detail = {
    name: 'admin.site-detail',
    url: '/sites/:siteId',
    component: 'siteDetail',
    data: { roles: ['Admin'] },
    resolve: {
      manufacturers: ['ManufacturerService', ManufacturerService => ManufacturerService.getAll()],
      site: [
        '$stateParams',
        'SiteService',
        ($stateParams, SiteService) => SiteService.get($stateParams.siteId)
      ],
      $title: ['site', site => `Sites - ${site.name || 'New'}`]
    }
  };

  const admin_units = {
    name: 'admin.epx-units',
    url: '/epx-units',
    component: 'epxUnits',
    data: { roles: ['Admin'] },
    resolve: {
      $title: () => 'EPX Units',
      epxUnits: ['EpxUnitService', EpxUnitService => EpxUnitService.getAll({ include: 'group' })]
    }
  };

  const admin_unit_detail = {
    name: 'admin.epx-unit-detail',
    url: '/epx-units/:unitId',
    component: 'epxUnitDetail',
    data: { roles: ['Admin'] },
    resolve: {
      sites: ['SiteService', SiteService => SiteService.getAll()],
      manufacturers: ['ManufacturerService', ManufacturerService => ManufacturerService.getAll()],
      epxUnit: [
        '$stateParams',
        'EpxUnitService',
        ($stateParams, EpxUnitService) => EpxUnitService.get($stateParams.unitId)
      ],
      $title: [
        'epxUnit',
        epxUnit => `Epx Units - ${epxUnit.name || 'New'} - #${epxUnit.serial || ''}`
      ]
    }
  };

  const admin_report_schedules = {
    name: 'admin.report-schedules',
    url: '/report-schedules',
    component: 'reportSchedules',
    data: { roles: ['Admin'] },
    resolve: {
      $title: () => 'Report Schedules',
      reportSchedules: [
        'ReportScheduleService',
        ReportScheduleService => ReportScheduleService.getAll({ include: 'site' })
      ]
    }
  };

  const admin_report_schedule_detail = {
    name: 'admin.report-schedule_detail',
    url: '/report-schedules/:reportScheduleId',
    component: 'reportScheduleDetail',
    data: { roles: ['Admin'] },
    resolve: {
      reportSchedule: [
        'ReportScheduleService',
        '$stateParams',
        (ReportScheduleService, $stateParams) =>
          ReportScheduleService.get($stateParams.reportScheduleId, {
            include: 'site'
          })
      ],
      sites: ['SiteService', SiteService => SiteService.getAll()],
      $title: [
        'reportSchedule',
        reportSchedule => `Report Schedules - ${reportSchedule.reportName || 'New'}`
      ]
    }
  };

  //USER  ROUTES
  const app = {
    name: 'app',
    url: '/site/:siteId',
    abstract: true,
    onEnter: [
      '$stateParams',
      'SiteSwitcherService',
      ($stateParams, SiteSwitcherService) => {
        const desiredSiteId = $stateParams.siteId;
        SiteSwitcherService.changeSite(desiredSiteId);
      }
    ],
    resolve: {
      currentSite: [
        'SiteSwitcherService',
        SiteSwitcherService => SiteSwitcherService.currentSite()
      ],
      currentUser: ['AuthService', AuthService => AuthService.currentUser()]
    }
  };

  const epx = {
    name: 'app.epx',
    url: '/epx',
    abstract: true,
    component: 'epx'
  };

  const subscriptions = {
    name: 'app.epx.subscriptions',
    url: '/subscriptions',
    component: 'epx.subscriptions',
    resolve: {
      subscriptions: [
        'SubscriptionService',
        'currentSite',
        (SubscriptionService, currentSite) => SubscriptionService.getAll({ site: currentSite.id })
      ],
      $title: ['currentSite', currentSite => `Subscriptions - ${currentSite.name}`]
    }
  };

  const subscription_detail = {
    name: 'app.epx.subscriptionDetail',
    url: '/subscriptions/:subscriptionId',
    component: 'epx.subscriptionDetail',
    resolve: {
      subscription: [
        'SubscriptionService',
        '$stateParams',
        (SubscriptionService, $stateParams) =>
          SubscriptionService.get($stateParams.subscriptionId, { include: 'site' })
      ],
      $title: ['currentSite', currentSite => `Subscriptions - ${currentSite.name}`]
    }
  };

  const reports = {
    name: 'app.epx.reports',
    url: '/reports?startDate&endDate',
    component: 'epx.reports',
    params: {
      startDate: moment()
        .subtract(7, 'days')
        .startOf('day')
        .format(),
      endDate: moment()
        .endOf('day')
        .format()
    },
    resolve: {
      $title: () => 'Reports',
      reports: [
        '$stateParams',
        'currentSite',
        'ReportService',
        ($stateParams, currentSite, ReportService) => {
          let startString = $stateParams.startDate.replace(' ', '+');
          let endString = $stateParams.endDate.replace(' ', '+');

          let filter: any = { site: currentSite.id };
          filter.time_min = startString;
          filter.time_max = endString;

          return ReportService.getAll(filter);
        }
      ]
    }
  };

  $urlRouterProvider.otherwise('/login');

  $stateProvider
    .state(app)
    .state(epx)
    .state(reports)
    .state(subscriptions)
    .state(subscription_detail)
    .state(admin)
    .state(admin_report_schedules)
    .state(admin_report_schedule_detail)
    .state(admin_sites)
    .state(admin_site_detail)
    .state(admin_users)
    .state(admin_user_detail)
    .state(admin_user_role)
    .state(admin_units)
    .state(admin_unit_detail)
    .state(login)
    .state(logout)
    .state(denied)
    .state(noRoute);
}

routes.$inject = ['$stateProvider', '$urlRouterProvider'];

angular.module('ciApp.routes', []).config(routes);



// WEBPACK FOOTER //
// ./routes.ts